import React, { useState, useEffect } from 'react'
import { useI18n } from '../../i18n'
import { useAuth } from '../../components/AuthProvider'
import { API_ENDPOINTS } from '../../config/api'
import { Globe, FileText, AlertCircle, CheckCircle, Clock, BarChart3, TrendingUp, AlertTriangle, Grid } from 'lucide-react'
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell } from 'recharts'
import { PageLayout } from '../../layouts/PageLayout'
import { Button } from '../../components/ui'

interface NationalProfile {
  id: string
  code: string
  title: string
  country: string
  maturityScale?: string
  compliancePercent: number
  categoriesCount: number
  questionsCount: number
  answeredCount: number
  lastUpdated?: string
}

interface ComplianceByCountry {
  country: string
  profileCode: string
  profileTitle: string
  compliancePercent: number
  answeredQuestions: number
  totalQuestions: number
}

interface TrendData {
  date: string
  [profileCode: string]: string | number
}

interface WeakArea {
  profileCode: string
  profileTitle: string
  categoryCode: string
  categoryTitle: string
  score: number
  questionsCount: number
}

interface MaturityMapItem {
  profileCode: string
  profileTitle: string
  categoryCode: string
  categoryTitle: string
  score: number
  level: 'High' | 'Medium' | 'Low' | 'Critical'
}

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899']

const GovernanceNationalProfiles: React.FC = () => {
  const { t } = useI18n()
  const { token } = useAuth()
  const [profiles, setProfiles] = useState<NationalProfile[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedProfile, setSelectedProfile] = useState<string | null>(null)
  
  // Analytics data
  const [complianceByCountry, setComplianceByCountry] = useState<ComplianceByCountry[]>([])
  const [trendData, setTrendData] = useState<TrendData[]>([])
  const [weakAreas, setWeakAreas] = useState<WeakArea[]>([])
  const [maturityMap, setMaturityMap] = useState<MaturityMapItem[]>([])
  const [analyticsLoading, setAnalyticsLoading] = useState(true)

  useEffect(() => {
    loadProfiles()
    loadAnalytics()
  }, [])

  const loadAnalytics = async () => {
    try {
      setAnalyticsLoading(true)
      
      // Load all analytics data in parallel
      const [complianceRes, trendRes, weakAreasRes, maturityMapRes] = await Promise.all([
        fetch(API_ENDPOINTS.GOVERNANCE_NATIONAL_PROFILES_ANALYTICS_COMPLIANCE_BY_COUNTRY, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(API_ENDPOINTS.GOVERNANCE_NATIONAL_PROFILES_ANALYTICS_TREND, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(API_ENDPOINTS.GOVERNANCE_NATIONAL_PROFILES_ANALYTICS_WEAK_AREAS, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(API_ENDPOINTS.GOVERNANCE_NATIONAL_PROFILES_ANALYTICS_MATURITY_MAP, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ])

      if (complianceRes.ok) {
        const complianceResult = await complianceRes.json()
        if (complianceResult.success) {
          setComplianceByCountry(complianceResult.data || [])
        }
      }

      if (trendRes.ok) {
        const trendResult = await trendRes.json()
        if (trendResult.success) {
          setTrendData(trendResult.data || [])
        }
      }

      if (weakAreasRes.ok) {
        const weakAreasResult = await weakAreasRes.json()
        if (weakAreasResult.success) {
          setWeakAreas(weakAreasResult.data || [])
        }
      }

      if (maturityMapRes.ok) {
        const maturityMapResult = await maturityMapRes.json()
        if (maturityMapResult.success) {
          setMaturityMap(maturityMapResult.data || [])
        }
      }
    } catch (err) {
      console.error('[GovernanceNationalProfiles] Analytics load error:', err)
    } finally {
      setAnalyticsLoading(false)
    }
  }

  const loadProfiles = async () => {
    try {
      setLoading(true)
      setError(null)
      const res = await fetch(API_ENDPOINTS.GOVERNANCE_NATIONAL_PROFILES, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      if (!res.ok) throw new Error('Failed to load national profiles')
      const result = await res.json()
      setProfiles(result.data || [])
    } catch (err: any) {
      setError(err.message || t('governance.nationalProfiles.error.loadFailed', 'Failed to load national profiles'))
      console.error('[GovernanceNationalProfiles] Error:', err)
    } finally {
      setLoading(false)
    }
  }

  const getCountryFlag = (country: string) => {
    const flags: Record<string, string> = {
      ES: '🇪🇸',
      DE: '🇩🇪',
      FR: '🇫🇷',
      LT: '🇱🇹',
      EE: '🇪🇪',
    }
    return flags[country] || '🌍'
  }

  const getComplianceColor = (percent: number) => {
    if (percent >= 80) return 'text-green-600'
    if (percent >= 60) return 'text-yellow-600'
    if (percent > 0) return 'text-orange-600'
    return 'text-gray-400'
  }

  const getBarColor = (percent: number) => {
    if (percent >= 80) return '#10b981'
    if (percent >= 60) return '#f59e0b'
    if (percent > 0) return '#ef4444'
    return '#9ca3af'
  }

  const getLevelColor = (level: string) => {
    switch (level) {
      case 'High': return 'bg-green-100 text-green-800 border-green-300'
      case 'Medium': return 'bg-yellow-100 text-yellow-800 border-yellow-300'
      case 'Low': return 'bg-orange-100 text-orange-800 border-orange-300'
      case 'Critical': return 'bg-red-100 text-red-800 border-red-300'
      default: return 'bg-gray-100 text-gray-800 border-gray-300'
    }
  }

  // Prepare trend data for line chart (extract profile codes from first entry)
  const trendChartData = trendData.map(item => {
    const result: any = { date: item.date }
    Object.keys(item).forEach(key => {
      if (key !== 'date' && typeof item[key] === 'number') {
        result[key] = item[key]
      }
    })
    return result
  })

  const trendKeys = trendData.length > 0 
    ? Object.keys(trendData[0]).filter(key => key !== 'date')
    : []

  // Group maturity map by profile for display
  const maturityMapByProfile = maturityMap.reduce((acc, item) => {
    if (!acc[item.profileCode]) {
      acc[item.profileCode] = {
        profileTitle: item.profileTitle,
        categories: []
      }
    }
    acc[item.profileCode].categories.push(item)
    return acc
  }, {} as Record<string, { profileTitle: string; categories: MaturityMapItem[] }>)

  if (loading) {
    return (
      <PageLayout pageScope="governance-national-profiles-page" title={t('governance.nationalProfiles.title', 'National Compliance Profiles')}>
        <p className="text-gray-600">{t('common.loading', 'Loading...')}</p>
      </PageLayout>
    )
  }

  if (error) {
    return (
      <PageLayout pageScope="governance-national-profiles-page" title={t('governance.nationalProfiles.title', 'National Compliance Profiles')}>
        <div className="p-4 bg-red-50 border border-red-200 rounded text-red-700">
          {error}
        </div>
      </PageLayout>
    )
  }

  return (
    <PageLayout pageScope="governance-national-profiles-page" title={t('governance.nationalProfiles.title', 'National Compliance Profiles')}
      description={t('governance.nationalProfiles.description', 'Manage national compliance frameworks (ENS, BSI, etc.)')}
    >
      {/* Profiles Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {profiles.map((profile) => (
          <div
            key={profile.id}
            className="ds-card p-6 cursor-pointer hover:shadow-lg transition-shadow"
            onClick={() => setSelectedProfile(selectedProfile === profile.id ? null : profile.id)}
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <Globe className="w-8 h-8 text-blue-600" />
                <div>
                  <h3 className="text-lg font-semibold">{profile.title}</h3>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-2xl">{getCountryFlag(profile.country)}</span>
                    <span className="text-sm text-gray-500">{profile.country}</span>
                    {profile.maturityScale && (
                      <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                        {profile.maturityScale}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">
                  {t('governance.nationalProfiles.compliance', 'Compliance')}:
                </span>
                <span className={`text-lg font-bold ${getComplianceColor(profile.compliancePercent)}`}>
                  {profile.compliancePercent}%
                </span>
              </div>

              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">
                  {t('governance.nationalProfiles.categories', 'Categories')}:
                </span>
                <span className="font-medium">{profile.categoriesCount}</span>
              </div>

              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">
                  {t('governance.nationalProfiles.questions', 'Questions')}:
                </span>
                <span className="font-medium">
                  {profile.answeredCount} / {profile.questionsCount}
                </span>
              </div>

              {profile.lastUpdated && (
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  <Clock className="w-4 h-4" />
                  <span>
                    {t('governance.nationalProfiles.lastUpdated', 'Last updated')}: {new Date(profile.lastUpdated).toLocaleDateString()}
                  </span>
                </div>
              )}

              <div className="pt-3 border-t">
                <Button
                  type="button"
                  color="primary"
                  fullWidth
                  onClick={(e) => {
                    e.stopPropagation()
                    // TODO: Navigate to profile detail page
                    console.log('Open profile:', profile.code)
                  }}
                >
                  {t('governance.nationalProfiles.openProfile', 'Open Profile')}
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {profiles.length === 0 && (
        <div className="text-center py-12">
          <Globe className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-600">
            {t('governance.nationalProfiles.noProfiles', 'No national compliance profiles available')}
          </p>
        </div>
      )}

      {/* Analytics Section */}
      {profiles.length > 0 && (
        <div className="mt-8 space-y-8">
          <h2 className="text-xl font-bold mb-6">
            {t('governance.nationalProfiles.analytics.title', 'Analytics & Insights')}
          </h2>

          {/* Charts Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Compliance by Country */}
            <div className="ds-card p-6">
              <div className="flex items-center gap-2 mb-4">
                <BarChart3 className="w-5 h-5 text-blue-600" />
                <h3 className="text-lg font-semibold">
                  {t('governance.nationalProfiles.analytics.complianceByCountry', 'Compliance by Country')}
                </h3>
              </div>
              {analyticsLoading ? (
                <div className="h-[300px] flex items-center justify-center">
                  <p className="text-gray-500">{t('common.loading', 'Loading...')}</p>
                </div>
              ) : complianceByCountry.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={complianceByCountry}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="country" />
                    <YAxis domain={[0, 100]} />
                    <Tooltip formatter={(value: number) => `${value}%`} />
                    <Legend />
                    <Bar dataKey="compliancePercent" name={t('governance.nationalProfiles.compliance', 'Compliance')}>
                      {complianceByCountry.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={getBarColor(entry.compliancePercent)} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[300px] flex items-center justify-center text-gray-500">
                  {t('common.noData', 'No data available')}
                </div>
              )}
            </div>

            {/* National Profile Trend */}
            <div className="ds-card p-6">
              <div className="flex items-center gap-2 mb-4">
                <TrendingUp className="w-5 h-5 text-green-600" />
                <h3 className="text-lg font-semibold">
                  {t('governance.nationalProfiles.analytics.trend', 'National Profile Trend')}
                </h3>
              </div>
              {analyticsLoading ? (
                <div className="h-[300px] flex items-center justify-center">
                  <p className="text-gray-500">{t('common.loading', 'Loading...')}</p>
                </div>
              ) : trendChartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={trendChartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis domain={[0, 100]} />
                    <Tooltip formatter={(value: number) => `${value}%`} />
                    <Legend />
                    {trendKeys.map((key, index) => (
                      <Line
                        key={key}
                        type="monotone"
                        dataKey={key}
                        stroke={COLORS[index % COLORS.length]}
                        strokeWidth={2}
                        name={key}
                      />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[300px] flex items-center justify-center text-gray-500">
                  {t('common.noData', 'No data available')}
                </div>
              )}
            </div>
          </div>

          {/* Weak Areas */}
          {weakAreas.length > 0 && (
            <div className="ds-card p-6">
              <div className="flex items-center gap-2 mb-4">
                <AlertTriangle className="w-5 h-5 text-orange-600" />
                <h3 className="text-lg font-semibold">
                  {t('governance.nationalProfiles.analytics.weakAreas', 'Weak Areas by Profile')}
                </h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left p-2">{t('governance.nationalProfiles.profile', 'Profile')}</th>
                      <th className="text-left p-2">{t('governance.nationalProfiles.category', 'Category')}</th>
                      <th className="text-right p-2">{t('governance.nationalProfiles.score', 'Score')}</th>
                      <th className="text-right p-2">{t('governance.nationalProfiles.questions', 'Questions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {weakAreas.map((area, index) => (
                      <tr key={index} className="border-b hover:bg-gray-50">
                        <td className="p-2">{area.profileTitle}</td>
                        <td className="p-2">{area.categoryTitle}</td>
                        <td className={`p-2 text-right font-semibold ${getComplianceColor(area.score)}`}>
                          {area.score}%
                        </td>
                        <td className="p-2 text-right">{area.questionsCount}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Profile Maturity Map */}
          {Object.keys(maturityMapByProfile).length > 0 && (
            <div className="ds-card p-6">
              <div className="flex items-center gap-2 mb-4">
                <Grid className="w-5 h-5 text-purple-600" />
                <h3 className="text-lg font-semibold">
                  {t('governance.nationalProfiles.analytics.maturityMap', 'Profile Maturity Map')}
                </h3>
              </div>
              <div className="space-y-6">
                {Object.entries(maturityMapByProfile).map(([profileCode, profileData]) => (
                  <div key={profileCode} className="border-b last:border-0 pb-4 last:pb-0">
                    <h4 className="font-semibold mb-3">{profileData.profileTitle}</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                      {profileData.categories.map((item, index) => (
                        <div
                          key={index}
                          className={`p-3 rounded border ${getLevelColor(item.level)}`}
                        >
                          <div className="text-sm font-medium">{item.categoryTitle}</div>
                          <div className="text-xs mt-1">
                            {t('governance.nationalProfiles.score', 'Score')}: {item.score}%
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </PageLayout>
  )
}

export default GovernanceNationalProfiles

